import { useState } from "react";
import { Sparkles, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import BrandHeader from "@/components/BrandHeader";
import SoftButton from "@/components/SoftButton";
import { addFutureFund, deleteFutureFund, FUND_TYPES, listFutureFunds, updateFutureFund } from "@/lib/futureFunds";

const EMPTY = { title: "", type: "Experience", why: "", when: "", nextStep: "", timeAttention: "", goal: "", setAside: "" };

export default function FutureFunds() {
  const [funds, setFunds] = useState(() => listFutureFunds());
  const [draft, setDraft] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [moneyOpen, setMoneyOpen] = useState(false);

  const save = () => {
    try {
    if (!draft.title.trim()) return;
    const entry = addFutureFund(draft);
    setFunds((current) => [entry, ...current]);
    setDraft(EMPTY);
    setMoneyOpen(false);
    setOpen(false);
    toast.success("Something to look forward to.", { description: "Keep it here at your pace." });
    } catch { toast.error("Could not save on this device. Your changes have not been stored."); }
  };

  const remove = (id) => {
    try {
    setFunds(deleteFutureFund(id));
    } catch { toast.error("Could not save on this device. Your changes have not been stored."); }
  };

  const addSaved = (item) => {
    try {
    const amount = Number(item.setAside || 0);
    if (!amount) return;
    const updated = updateFutureFund(item.id, { saved: Number(item.saved || 0) + amount });
    setFunds((current) => current.map((fund) => (fund.id === item.id ? updated : fund)));
    } catch { toast.error("Could not save on this device. Your changes have not been stored."); }
  };

  return (
    <PageContainer className="pt-7" testid="future-funds-page">
      <BrandHeader compact />

      <section className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.34em] mb-2" style={{ color: "var(--un-text-3)" }}>Look Forward</p>
        <h1 className="text-[43px] leading-[1.01] tracking-[-0.035em]" style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}>
          Give future joy
          <br />
          <em className="font-normal" style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}>a place in today.</em>
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--un-text-2)" }}>
          A moment, a place, a person, an experience, some rest, something you want to learn, create, try, or simply enjoy. It does not have to cost anything.
        </p>
      </section>

      <section className="rounded-[1.75rem] p-5 mb-5" style={{ background: "rgba(91,57,84,0.045)", border: "1px solid rgba(91,57,84,0.06)" }}>
        <div className="flex gap-3 items-start">
          <span className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(214,184,132,0.17)", color: "var(--un-ember)" }}>
            <Sparkles size={18} />
          </span>
          <div>
            <p className="text-[16px] font-medium" style={{ color: "var(--un-text-1)" }}>Money is optional here</p>
            <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
              Start with what you want to make room for. If money is relevant, you can note an amount below. UnAnonymous does not hold, transfer, or protect money.
            </p>
          </div>
        </div>
      </section>

      {!open && (
        <SoftButton variant="primary" size="lg" className="w-full mb-5" iconLeft={<Plus size={17} />} onClick={() => setOpen(true)}>
          Add something to look forward to
        </SoftButton>
      )}

      {open && (
        <section className="un-card p-5 mb-5">
          <input
            value={draft.title}
            onChange={(e) => setDraft((current) => ({ ...current, title: e.target.value }))}
            placeholder="A rest day, dinner with a friend, a class, a concert…"
            className="w-full rounded-2xl p-3 outline-none text-[15px] mb-3"
            style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
            autoFocus
          />
          <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
            {FUND_TYPES.map((type) => (
              <button
                key={type}
                onClick={() => setDraft((current) => ({ ...current, type }))}
                className="shrink-0 px-3 py-1.5 rounded-full text-[11px] un-press"
                style={{
                  background: draft.type === type ? "var(--un-plum)" : "rgba(91,57,84,0.05)",
                  color: draft.type === type ? "#fffaf5" : "var(--un-text-2)",
                }}
              >
                {type}
              </button>
            ))}
          </div>
          <textarea
            value={draft.why}
            onChange={(e) => setDraft((current) => ({ ...current, why: e.target.value }))}
            placeholder="Why would this feel good or meaningful? (optional)"
            rows={2}
            className="w-full rounded-2xl p-3 outline-none resize-none text-[14px] mb-3"
            style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
          />
          <div className="grid grid-cols-1 gap-2">
            <input
              value={draft.when}
              onChange={(e) => setDraft((current) => ({ ...current, when: e.target.value }))}
              placeholder="When, if you want to name it (optional)"
              className="w-full rounded-2xl p-3 outline-none text-[14px]"
              style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
            />
            <input
              value={draft.nextStep}
              onChange={(e) => setDraft((current) => ({ ...current, nextStep: e.target.value }))}
              placeholder="One small step you might take (optional)"
              className="w-full rounded-2xl p-3 outline-none text-[14px]"
              style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
            />
            <input
              value={draft.timeAttention}
              onChange={(e) => setDraft((current) => ({ ...current, timeAttention: e.target.value }))}
              placeholder="Time or attention you might make room for (optional)"
              className="w-full rounded-2xl p-3 outline-none text-[14px]"
              style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
            />
          </div>

          <button
            type="button"
            onClick={() => setMoneyOpen((value) => !value)}
            className="mt-3 text-[12px] px-3 py-2 rounded-full un-press"
            style={{ background: "rgba(91,57,84,0.05)", color: "var(--un-plum)" }}
            aria-expanded={moneyOpen}
          >
            {moneyOpen ? "Hide money note" : "Add an optional money note"}
          </button>

          {moneyOpen && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              <input
                inputMode="decimal"
                value={draft.goal}
                onChange={(e) => setDraft((current) => ({ ...current, goal: e.target.value }))}
                placeholder="Amount in mind"
                aria-label="Optional amount in mind"
                className="w-full rounded-2xl p-3 outline-none text-[14px]"
                style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
              />
              <input
                inputMode="decimal"
                value={draft.setAside}
                onChange={(e) => setDraft((current) => ({ ...current, setAside: e.target.value }))}
                placeholder="Amount at a time"
                aria-label="Optional amount to set aside at a time"
                className="w-full rounded-2xl p-3 outline-none text-[14px]"
                style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 mt-4">
            <SoftButton variant="ghost" size="md" onClick={() => { setOpen(false); setDraft(EMPTY); setMoneyOpen(false); }}>Cancel</SoftButton>
            <SoftButton variant="primary" size="md" onClick={save} disabled={!draft.title.trim()}>Keep it</SoftButton>
          </div>
        </section>
      )}

      <div className="space-y-3 mb-8">
        {funds.length === 0 ? (
          <div className="text-center py-8 px-5">
            <p className="text-[17px]" style={{ color: "var(--un-text-1)", fontFamily: "Lora, serif" }}>What would you like to make a little room for?</p>
            <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
              It can be a moment, a place, a person, an experience, some rest, or something you want to learn, create, try, or enjoy. It does not have to cost anything.
            </p>
          </div>
        ) : funds.map((item) => {
          const pct = item.goal ? Math.min(100, Math.round((Number(item.saved || 0) / Number(item.goal)) * 100)) : null;
          return (
            <section key={item.id} className="un-card p-5">
              <div className="flex justify-between gap-3 items-start">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--un-burgundy)" }}>{item.type}</p>
                  <p className="mt-1 text-[17px] font-medium" style={{ color: "var(--un-text-1)" }}>{item.title}</p>
                  {item.why && <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>{item.why}</p>}
                  {item.when && <p className="mt-2 text-[11px]" style={{ color: "var(--un-text-3)" }}>When: {item.when}</p>}
                  {item.nextStep && <p className="mt-1 text-[11px]" style={{ color: "var(--un-text-3)" }}>Possible next step: {item.nextStep}</p>}
                  {item.timeAttention && <p className="mt-1 text-[11px]" style={{ color: "var(--un-text-3)" }}>Time or attention: {item.timeAttention}</p>}
                </div>
                <button onClick={() => remove(item.id)} aria-label="Remove" className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "rgba(117,55,66,0.05)", color: "var(--un-burgundy)" }}>
                  <Trash2 size={13} />
                </button>
              </div>
              {(item.goal || item.setAside) && (
                <div className="mt-4">
                  <div className="flex justify-between text-[11px]" style={{ color: "var(--un-text-3)" }}>
                    <span>${Number(item.saved || 0).toFixed(0)} set aside</span>
                    {item.goal ? <span>${Number(item.goal).toFixed(0)} in mind</span> : null}
                  </div>
                  {pct !== null && (
                    <div className="h-2 rounded-full mt-2 overflow-hidden" style={{ background: "rgba(91,57,84,0.06)" }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "var(--un-plum)" }} />
                    </div>
                  )}
                  {item.setAside ? (
                    <button onClick={() => addSaved(item)} className="mt-3 text-[12px] px-3 py-2 rounded-full un-press" style={{ background: "rgba(91,57,84,0.06)", color: "var(--un-plum)" }}>
                      Add ${Number(item.setAside).toFixed(0)} today
                    </button>
                  ) : null}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </PageContainer>
  );
}
